/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
// import { Chart as ChartJS, registerables } from 'chart.js';
// import { Chart } from 'react-chartjs-2'
// ChartJS.register(...registerables);
// import 'chartjs-adapter-moment';

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

//Backend access
import { getLogsByField, loadToken } from 'services/api/bks';
import moment from "moment";

import GradientMinMaxLineChart from "components/Charts/GradientMinMaxLineChart/stats"

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
// import HighChartBarChart from "../highchart";

function DeviceStatsChart({ device, field, advancedToggle, startTime, setStartTime, endTime, setEndTime, groupSize, setGroupSize, height }) {
  // console.log(groupSize)
  loadToken(useAuth0());

  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [min_value, setMinChartValue] = useState({});
  const [max_value, setMaxChartValue] = useState({});
  const [usedFields, setUsedFields] = useState([]);
  const [loading, setLoading] = useState(true);

  async function processChartData(data, device, server_name) {

    var chart_data = {
      labels: [],
      datasets: []
    }

    var datasets = {
      "min": {},
      "max": {},
      "mean": {}
    };

    var max = {};
    var min = {};

    var fields = server_name.split(',');

    if (!advancedToggle) {
      var temp = fields[0];
      fields = [temp];
    }

    var field_datasets = {};

    for (var field_nm of fields) {
      datasets["min"][field_nm] = [];
      datasets["max"][field_nm] = [];
      datasets["mean"][field_nm] = [];

      for (var index in data) {
        var field_device = data[index];

        for (var value in field_device.data) {
          if (field_device.data[value]?.[field_nm]) {
            var gap = field_device.data[value].start / field_device.data[value]["_id"];

            if (typeof field.transform_data == 'function') {
              field_device.data[value][field_nm] = field.transform_data(device, advancedToggle, field_device.data[value][field_nm], field_nm);
            }

            var timeslot = field_device.data[value].start + gap;
            if (timeslot > moment().unix()) {
              timeslot = moment().unix();
            }

            for (var key in datasets) {
              datasets[key][field_nm].push({
                'y': field_device.data[value][field_nm][key],
                [`y${fields.indexOf(field_nm) + 1}`]: field_device.data[value][field_nm][key],
                'x': timeslot * 1000
              });
            }
          }
        }
      }

      max[field_nm] = datasets.max[field_nm].length > 0 ? Math.max(...datasets.max[field_nm].map(value => value[`y${fields.length > 1 ? fields.indexOf(field_nm) + 1 : ""}`])) : null;
      min[field_nm] = datasets.min[field_nm].length > 0 ? Math.min(...datasets.min[field_nm].map(value => value[`y${fields.length > 1 ? fields.indexOf(field_nm) + 1 : ""}`])) : null;
    }

    var used_fields = [...new Set(Object.entries(min).filter(([k, v]) => v != null).map(([k, v]) => k).concat(Object.entries(max).filter(([k, v]) => v != null).map(([k, v]) => k)))];

    var applyGradient = (context) => {
      if (!context.chart.chartArea) {
        return;
      }
      const { ctx, chartArea: { top, bottom } } = context.chart;
      var gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

      field.gradient(gradientBg, used_fields);
      return gradientBg;
    };
    
    for (var field_nm of used_fields) {
      field_datasets[field_nm] = [
        {
          label: "Lower Range",
          backgroundColor: applyGradient,
          borderColor: applyGradient,
          data: datasets["min"][field_nm] || [],
          fill: '+1',
          type: 'line',
          radius: 0,
          order: 2,
          tension: 0.3,
          categoryPercentage: 1.0,
          barPercentage: 1.0,
          borderSkipped: false
        },
        {
          label: "Upper Range",
          backgroundColor: applyGradient,
          borderColor: applyGradient,
          data: datasets["max"][field_nm] || [],
          fill: '-1',
          type: 'line',
          radius: 0,
          order: 2,
          tension: 0.3,
          categoryPercentage: 1.0,
          barPercentage: 1.0,
          borderSkipped: false
        },
        {
          label: 'Average',
          data: datasets["mean"][field_nm] || [],
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderColor: "rgba(0, 0, 0, 0.4)",
          type: 'line',
          order: 1,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 10,
        }
      ]
    }

    for (var field_nm in field_datasets) {
      const axisID = used_fields.length > 1 ? `y${used_fields.indexOf(field_nm) + 1}` : 'y';

      // Explicitly update each dataset object inside the array
      const updatedDatasets = field_datasets[field_nm].map((dataset) => ({
        ...dataset,
        yAxisID: axisID,
        parsing: {
          xAxisKey: 'x',   // Explicitly map your time coordinates
          yAxisKey: axisID // Explicitly map your custom value key
        }
      }));

      chart_data.datasets = chart_data.datasets.concat(updatedDatasets);
    }

    return { chart_data, min, max, used_fields };
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      // console.log(device.uuid, field.server_name, startTime.unix(), endTime.unix(), groupSize);

      var data = await getLogsByField(device.uuid, field.server_name, startTime.unix(), endTime.unix(), groupSize);
      // console.log(data);
      var { chart_data, min, max, used_fields } = await processChartData(data, device, field.server_name);
      // console.log(chart_data, min, max)
      setChartData(chart_data);
      setMinChartValue(min);
      setMaxChartValue(max);
      setUsedFields(used_fields);
      setLoading(false);
    };
    fetchData();
  }, [startTime, endTime, field, advancedToggle]);

  var fields = field.server_name.split(',');

  if (!advancedToggle) {
    var temp = usedFields.length ? usedFields[0] : fields[0];
    fields = [temp];
  }

  if (field.field == 'battery') {
    if (!advancedToggle) {
      field.min = field.suggestedMin['battery'];
      field.max = field.suggestedMax['battery'];
    }
    else {
      field.min = undefined;
      field.max = undefined;
    }
  }

  return (
    <Card>
      <SoftBox p={{ xxs: 1, xs: 2 }}>
        <SoftBox sx={{ opacity: loading ? 0.3 : 1 }}>
          <SoftBox m={1} display="flex" justifyContent="center" alignItems="center">
            <SoftTypography variant="h6">
              {field.label}
            </SoftTypography>
          </SoftBox>
          <SoftBox sx={{ height }}>
            {chartData && <GradientMinMaxLineChart key={advancedToggle ? 'multi' : 'single'} chartData={chartData} field={field} advancedToggle={advancedToggle} min={field?.min} suggestedMin={field.suggestedMin} min_value={min_value} max={field?.max} suggestedMax={field.suggestedMax} max_value={max_value} startTime={startTime.unix() * 1000} endTime={endTime.unix() * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} setGroupSize={setGroupSize} />}
          </SoftBox>
          {/* {chartData && <HighChartBarChart chartData={chartData} field={field.field} />} */}
        </SoftBox>
      </SoftBox>
    </Card>
  );

}

// Typechecking props for the DeviceStatsChart
DeviceStatsChart.propTypes = {
  device: PropTypes.object,
  field: PropTypes.object,
  advancedToggle: PropTypes.bool,
  startTime: PropTypes.instanceOf(moment()),
  setStartTime: PropTypes.func,
  endTime: PropTypes.instanceOf(moment()),
  setEndTime: PropTypes.func,
  groupSize: PropTypes.number,
  setGroupSize: PropTypes.func,
  height: PropTypes.number
};

export default DeviceStatsChart;

